/* « Catégorie » — one optional category per campaign (wireframes-fully ruling § 6, WF-042; Technical's 0039; lib-pack-23's
   lib/organisation). Built beside the étiquettes, in the same « Organisation interne (facultatif) » card: on the builder's
   step 3 (a draft) and in a published campaign's Paramètres tab. One value, picked from the workspace's list (« Aucune
   catégorie » clears it); who writes the list can add one inline and pick it. Row data: a change is written to the campaign
   at once (setCampaignCategory: PATCH { categoryId }) and never touches its content — no « Modifications à publier », no
   version. Before the draft exists the choice is held here and written when its id arrives. Never shown to a customer,
   never a rule of the parcours. Campaigns only: a gift type keeps its fixed category (ruling Q2 (a)).
   Nothing is drawn while useOrganisationReady() is false (staging before 0039: the list is not there yet).
   Also: the lists' « Catégorie » select (CategorieFilter) and a row's category (CategorieLabel). */
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Input, Select } from '@/ui'
import { useLocations } from '@/lib/api'
import {
  addCategory, campaignCategory, canWriteCategoryList, setCampaignCategory, useCategories, useOrganisationReady, useOrganisationStore,
  CATEGORY_NAME_MAX,
} from '@/lib/organisation'
import { editRefusalLines, settled, writeMark } from '@/lib/writes'

export function CategoriePicker({ id, canEdit }: {
  /** the campaign's id; null while the draft does not exist yet */
  id: string | null
  /** the campaign's own write rights (canBuild) */
  canEdit: boolean
}) {
  const list = useCategories()
  const ready = useOrganisationReady()
  const { data: locations } = useLocations()
  const listWriter = canWriteCategoryList((locations ?? []).map(l => l.id))
  const [held, setHeld] = useState<string | null>(null)
  const [refusal, setRefusal] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  /* the choice before the draft exists is written once its id arrives; a renamed id (the server's name for a draft) keeps
     the first one's category */
  const prev = useRef<string | null>(id)
  useEffect(() => {
    const was = prev.current; prev.current = id
    if (!id) return
    if (!was && held) { setCampaignCategory(id, held); setHeld(null) }
    else if (was && was !== id && !campaignCategory(id)) { const moved = campaignCategory(was); if (moved) setCampaignCategory(id, moved.id) }
  }, [id]) // eslint-disable-line react-hooks/exhaustive-deps
  if (!ready) return null
  const chosen: string | null = id ? campaignCategory(id)?.id ?? null : held
  /** with an API, a refused write is said here once the server answers */
  const hear = (mark: number) => { void settled(mark).then(no => { if (no) setRefusal(editRefusalLines(no)[0]) }) }
  const put = (catId: string | null, mark = writeMark()) => {
    setRefusal(null)
    if (!id) { setHeld(catId); return }
    const r = setCampaignCategory(id, catId)
    if (r) { setRefusal(r); return }
    hear(mark)
  }
  const create = () => {
    const mark = writeMark()
    const r = addCategory(draft)
    if (typeof r === 'string') { setRefusal(r); return }
    setDraft('')
    put(r.id, mark)
    if (!id) hear(mark)
  }
  const name = list.find(c => c.id === chosen)?.name
  return (
    <div data-testid="categorie-picker" style={{ display: 'grid', gap: 8 }}>
      {canEdit
        ? <label className="field" style={{ margin: 0 }}><span className="f-label">Catégorie</span>
          <Select aria-label="Catégorie" data-testid="categorie-choix" value={chosen ?? ''} onChange={e => put(e.target.value || null)} style={{ maxWidth: 320 }}>
            <option value="">Aucune catégorie</option>
            {list.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select></label>
        : <p className="f-help" style={{ margin: 0 }} data-testid="categorie-lecture">Catégorie&nbsp;: {name ?? 'aucune'}</p>}
      {canEdit && listWriter && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <Input aria-label="Nouvelle catégorie" placeholder="Nouvelle catégorie" value={draft} maxLength={CATEGORY_NAME_MAX + 16} style={{ flex: '1 1 160px', minWidth: 0, maxWidth: 260 }}
            data-testid="categorie-nouvelle"
            onChange={e => { setDraft(e.target.value); setRefusal(null) }} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); create() } }} />
          <Button size="sm" onClick={create} disabled={!draft.trim()} data-testid="categorie-ajouter">Ajouter</Button>
          <Link to="/parametres/organisation" className="f-help">Gérer les catégories</Link>
        </div>
      )}
      {refusal && <p className="f-help err" role="alert" style={{ margin: 0 }} data-testid="categorie-refus">{refusal}</p>}
    </div>
  )
}

/** A row's category, in its meta line (never a customer's sight); nothing without one. */
export function CategorieLabel({ id, row }: { id: string; row?: { categoryId?: string | null } }) {
  useOrganisationStore()
  if (!useOrganisationReady()) return null
  const c = campaignCategory(id, row)
  return c ? <span className="listrow-meta" data-testid="categorie">Catégorie&nbsp;: {c.name}</span> : null
}

/** The list's « Catégorie » select: '' = « Toutes les catégories ». Drawn only when the workspace has catégories. */
export function CategorieFilter({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  const list = useCategories()
  const ready = useOrganisationReady()
  if (!ready || !list.length) return null
  return <label className="field"><span className="f-label">Catégorie</span>
    <Select aria-label="Catégorie" data-testid="filtre-categorie" value={list.some(c => c.id === value) ? value : ''} onChange={e => onChange(e.target.value)}>
      <option value="">Toutes les catégories</option>
      {list.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
    </Select></label>
}
